import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import OwnerPicker from "../components/OwnerPicker";
import FloatingScrollbar from "../components/FloatingScrollbar";
import {
    Loader2, LogOut, Archive, ArchiveRestore, Trash2, RotateCcw,
    ChevronDown, ChevronRight, AlertTriangle, Globe,
} from "lucide-react";

const READINESS_STYLES = {
    Strong: "bg-ash-orange text-white",
    Warm: "bg-ash-black text-white",
    Nurture: "bg-ash-gray-light text-ash-black",
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [backfilling, setBackfilling] = useState(false);
    const [backfillMsg, setBackfillMsg] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/admin/submissions");
            setItems(data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("ash_admin_token");
                navigate("/admin/login");
                return;
            }
            setErr(e.response?.data?.detail || "Could not load submissions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateLocal = (id, patch) =>
        setItems((curr) => curr.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    const setMeta = async (id, patch) => {
        try {
            await api.patch(`/admin/submissions/${id}/meta`, patch);
            updateLocal(id, patch);
        } catch (e) {
            console.error(e);
        }
    };

    const archive = (id) => {
        if (!window.confirm("Move this submission to the Archived section? You can un-archive any time.")) return;
        setMeta(id, { archived: true });
    };
    const unarchive = (id) => setMeta(id, { archived: false });

    const softDelete = (id, org) => {
        if (!window.confirm(`Delete "${org}"? It will move to the Deleted section where you can restore it.`)) return;
        api.delete(`/admin/submissions/${id}`)
            .then(() => updateLocal(id, { deleted: true }))
            .catch((e) => alert(e.response?.data?.detail || "Delete failed"));
    };
    const restore = (id) => {
        api.post(`/admin/submissions/${id}/restore`)
            .then(() => updateLocal(id, { deleted: false }))
            .catch((e) => alert(e.response?.data?.detail || "Restore failed"));
    };
    const purge = (id, org) => {
        if (!window.confirm(`Permanently delete "${org}"? This cannot be undone.`)) return;
        if (!window.confirm("Really? This is permanent.")) return;
        api.delete(`/admin/submissions/${id}/purge`)
            .then(() => setItems((curr) => curr.filter((it) => it.id !== id)))
            .catch((e) => alert(e.response?.data?.detail || "Purge failed"));
    };

    const logout = () => {
        localStorage.removeItem("ash_admin_token");
        navigate("/admin/login");
    };

    const backfillAudits = async () => {
        const sitesNeeded = items.filter((i) => i.website && !i.deleted).length;
        if (!window.confirm(
            `Run Lighthouse audits on all submissions that don't have one yet? ` +
            `Up to ${sitesNeeded} submissions will be queued. ` +
            `Each audit takes 30-60 seconds and runs in the background.`
        )) return;
        setBackfilling(true);
        setBackfillMsg("");
        try {
            const { data } = await api.post("/admin/audit/backfill");
            setBackfillMsg(
                data.queued === 0
                    ? "Nothing to backfill — all submissions with a website already have a successful audit."
                    : `Queued ${data.queued} audits. They will run sequentially in the background — refresh in a few minutes to see results.`
            );
            setTimeout(load, 1500);
        } catch (e) {
            setBackfillMsg(e.response?.data?.detail || "Could not start backfill");
        } finally {
            setBackfilling(false);
            setTimeout(() => setBackfillMsg(""), 10000);
        }
    };

    const active = items.filter((i) => !i.archived && !i.deleted);
    const archived = items.filter((i) => i.archived && !i.deleted);
    const deleted = items.filter((i) => i.deleted);

    return (
        <Layout minimal>
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="admin-dashboard-section">
                <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-2">ASH Admin</div>
                        <h1 className="font-display font-black text-4xl tracking-tighter text-ash-black">Submissions</h1>
                        <p className="text-ash-gray-dark mt-2">
                            {active.length} active · {archived.length} archived · {deleted.length} deleted
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={backfillAudits}
                            disabled={backfilling}
                            className="btn-outline flex items-center gap-2"
                            data-testid="backfill-audits-btn"
                            title="Run Lighthouse audits on past submissions that don't have one yet"
                        >
                            {backfilling ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />}
                            Audit past submissions
                        </button>
                        <button onClick={logout} className="btn-outline flex items-center gap-2" data-testid="admin-logout-btn">
                            <LogOut size={16} /> Sign out
                        </button>
                    </div>
                </div>

                {backfillMsg && (
                    <div className="mb-6 bg-ash-orange-50 border border-ash-orange/30 text-ash-black text-sm p-3" data-testid="backfill-msg">
                        {backfillMsg}
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center gap-2 text-ash-gray-dark">
                        <Loader2 className="animate-spin" /> Loading…
                    </div>
                ) : err ? (
                    <div className="text-red-600">{err}</div>
                ) : (
                    <>
                        {active.length === 0 ? (
                            <div className="bg-white border border-ash-gray-light p-10 text-center" data-testid="admin-empty">
                                <div className="font-display font-bold text-2xl">No active submissions.</div>
                                <p className="text-ash-gray-dark mt-2">
                                    {archived.length || deleted.length
                                        ? "Check the Archived or Deleted sections below."
                                        : "Once people complete the checkup, they'll appear here."}
                                </p>
                            </div>
                        ) : (
                            <SubmissionsTable
                                items={active}
                                kind="active"
                                onArchive={archive}
                                onDelete={softDelete}
                                onSetReportSent={(id, v) => setMeta(id, { report_sent: v })}
                                onSetOwner={(id, v) => updateLocal(id, { owner: v })}
                            />
                        )}

                        {/* Archived section */}
                        {archived.length > 0 && (
                            <Collapsible
                                title={`Archived (${archived.length})`}
                                open={showArchived}
                                onToggle={() => setShowArchived((v) => !v)}
                                testid="archived-section"
                            >
                                <SubmissionsTable
                                    items={archived}
                                    kind="archived"
                                    onUnarchive={unarchive}
                                    onDelete={softDelete}
                                    onSetReportSent={(id, v) => setMeta(id, { report_sent: v })}
                                    onSetOwner={(id, v) => updateLocal(id, { owner: v })}
                                />
                            </Collapsible>
                        )}

                        {/* Deleted section */}
                        {deleted.length > 0 && (
                            <Collapsible
                                title={`Deleted (${deleted.length})`}
                                open={showDeleted}
                                onToggle={() => setShowDeleted((v) => !v)}
                                testid="deleted-section"
                            >
                                <SubmissionsTable
                                    items={deleted}
                                    kind="deleted"
                                    onRestore={restore}
                                    onPurge={purge}
                                />
                            </Collapsible>
                        )}
                    </>
                )}
            </section>
            {/* Single floating horizontal scrollbar — pinned to the viewport bottom,
                always visible while any table has overflow. Scroll syncs across
                active, archived, and deleted tables at once. */}
            {!loading && (
                <FloatingScrollbar
                    targetSelectors={[
                        '[data-testid="table-active"]',
                        '[data-testid="table-archived"]',
                        '[data-testid="table-deleted"]',
                    ]}
                />
            )}
        </Layout>
    );
}

const Collapsible = ({ title, open, onToggle, testid, children }) => (
    <div className="mt-10" data-testid={testid}>
        <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center justify-between bg-ash-off-white border border-ash-gray-light px-5 py-4 hover:border-ash-orange transition-colors"
            data-testid={`${testid}-toggle`}
        >
            <div className="flex items-center gap-2 text-left">
                {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className="font-display font-bold text-lg">{title}</span>
            </div>
            <span className="text-xs uppercase tracking-widest text-ash-gray-dark">
                {open ? "Hide" : "Show"}
            </span>
        </button>
        {open && <div className="mt-3">{children}</div>}
    </div>
);

const Th = ({ children, className = "" }) => (
    <th className={`px-3 py-3 text-xs uppercase tracking-widest font-bold text-ash-gray-dark whitespace-nowrap ${className}`}>
        {children}
    </th>
);
const Td = ({ children, className = "" }) => (
    <td className={`px-3 py-3 align-top ${className}`}>{children}</td>
);

const SubmissionsTable = ({
    items,
    kind, // "active" | "archived" | "deleted"
    onArchive,
    onUnarchive,
    onDelete,
    onRestore,
    onPurge,
    onSetReportSent,
    onSetOwner,
}) => (
    <div className="bg-white border border-ash-gray-light admin-table-scroll" data-testid={`table-${kind}`}>
        <table className="w-full text-sm">
            <thead className="bg-ash-off-white">
                <tr className="text-left">
                    <Th>Organization</Th>
                    <Th>Contact</Th>
                    <Th>Industry</Th>
                    <Th>Score</Th>
                    <Th>Profile</Th>
                    <Th>Lead</Th>
                    <Th>Owner</Th>
                    <Th>Report Sent</Th>
                    <Th>Submitted</Th>
                    <Th className="text-right">Actions</Th>
                </tr>
            </thead>
            <tbody>
                {items.map((s) => (
                    <tr key={s.id} className="border-t border-ash-gray-light hover:bg-ash-off-white" data-testid={`row-${s.id}`}>
                        <Td>
                            <div className="font-bold text-ash-black">{s.organization}</div>
                        </Td>
                        <Td>
                            <div className="text-ash-black whitespace-nowrap">
                                {s.first_name} {s.last_name}
                            </div>
                            <div className="text-xs text-ash-gray-dark">{s.email}</div>
                        </Td>
                        <Td className="text-ash-gray-dark whitespace-nowrap">{s.industry}</Td>
                        <Td>
                            <span className="font-display font-black text-ash-orange text-lg">{s.overall_score}</span>
                        </Td>
                        <Td className="text-ash-gray-dark whitespace-nowrap">{s.profile_name}</Td>
                        <Td>
                            <span
                                className={`text-xs uppercase tracking-widest font-bold px-2 py-1 ${READINESS_STYLES[s.lead_readiness] || "bg-ash-gray-light"}`}
                                data-testid={`lead-pill-${s.id}`}
                            >
                                {s.lead_readiness}
                            </span>
                        </Td>
                        <Td>
                            {kind === "deleted" ? (
                                <span className="text-ash-gray-dark">{s.owner || "—"}</span>
                            ) : (
                                <OwnerPicker
                                    submissionId={s.id}
                                    value={s.owner || ""}
                                    onSaved={(v) => onSetOwner && onSetOwner(s.id, v)}
                                    compact
                                />
                            )}
                        </Td>
                        <Td>
                            {kind === "deleted" ? (
                                <span className="text-ash-gray-dark text-xs">{s.report_sent ? "Yes" : "No"}</span>
                            ) : (
                                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`report-sent-label-${s.id}`}>
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5 accent-ash-orange"
                                        checked={!!s.report_sent}
                                        onChange={(e) => onSetReportSent(s.id, e.target.checked)}
                                        data-testid={`report-sent-${s.id}`}
                                    />
                                    {s.pdf_sent_at && (
                                        <span className="text-xs text-ash-gray-dark">
                                            sent {new Date(s.pdf_sent_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </label>
                            )}
                        </Td>
                        <Td className="text-xs text-ash-gray-dark whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</Td>
                        <Td className="text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1">
                                {kind === "deleted" ? (
                                    <>
                                        <IconBtn title="Restore" onClick={() => onRestore(s.id)} testid={`restore-${s.id}`}>
                                            <RotateCcw size={16} />
                                        </IconBtn>
                                        <IconBtn title="Permanently delete" danger onClick={() => onPurge(s.id, s.organization)} testid={`purge-${s.id}`}>
                                            <AlertTriangle size={16} />
                                        </IconBtn>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to={`/admin/submissions/${s.id}`}
                                            className="text-ash-orange font-bold underline px-2"
                                            data-testid={`view-${s.id}`}
                                        >
                                            View
                                        </Link>
                                        {kind === "active" ? (
                                            <IconBtn title="Archive" onClick={() => onArchive(s.id)} testid={`archive-${s.id}`}>
                                                <Archive size={16} />
                                            </IconBtn>
                                        ) : (
                                            <IconBtn title="Un-archive" onClick={() => onUnarchive(s.id)} testid={`unarchive-${s.id}`}>
                                                <ArchiveRestore size={16} />
                                            </IconBtn>
                                        )}
                                        <IconBtn title="Delete" danger onClick={() => onDelete(s.id, s.organization)} testid={`delete-${s.id}`}>
                                            <Trash2 size={16} />
                                        </IconBtn>
                                    </>
                                )}
                            </div>
                        </Td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const IconBtn = ({ children, onClick, title, danger = false, testid }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        className={`p-2 border border-transparent hover:border-ash-gray-light ${danger ? "text-red-600 hover:text-red-700" : "text-ash-gray-dark hover:text-ash-black"}`}
        data-testid={testid}
    >
        {children}
    </button>
);
